/**
 * Period arithmetic for the usage panels — AI tokens, Network requests, and
 * third-party API calls all page through the same hour/day/month/year windows.
 *
 * These lived as identical copies in TokenUsageReport.tsx and
 * NetworkRequestReport.tsx. A third panel would have made three, so this is the
 * one copy. Dependency-free on purpose: no React, no router, no api client, so
 * it is directly testable under CRA's jest (which cannot resolve
 * react-router-dom v7).
 *
 * Everything here works in UTC, because the backend buckets in UTC. Rendering a
 * period header in local time would label a chart with a day it isn't showing.
 */

export type Period = 'hour' | 'day' | 'month' | 'year';

/** Parse a `period_start` as UTC whether or not it carries a Z. */
function asUtc(periodStart: string): Date {
  return new Date(periodStart.endsWith('Z') ? periodStart : periodStart + 'Z');
}

/** The `period_start` one period earlier (-1) or later (+1). */
export function shiftPeriod(periodStart: string, period: Period, direction: -1 | 1): string {
  if (period === 'hour') {
    const d = asUtc(periodStart);
    d.setUTCHours(d.getUTCHours() + direction);
    return d.toISOString().slice(0, 19);
  }
  const [year, month, day] = periodStart.split('-').map(Number);
  if (period === 'day') {
    const d = new Date(Date.UTC(year, month - 1, day + direction));
    return d.toISOString().slice(0, 10);
  }
  if (period === 'month') {
    let m = month - 1 + direction;
    let y = year;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    return `${y}-${String(m + 1).padStart(2, '0')}-01`;
  }
  return `${year + direction}-01-01`;
}

/** The period as a person reads it, e.g. "August 2026" or "Aug 18, 2026 09:00–10:00". */
export function formatPeriodHeader(periodStart: string, period: Period): string {
  if (period === 'hour') {
    const d = asUtc(periodStart);
    const datePart = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const nextH = String((d.getUTCHours() + 1) % 24).padStart(2, '0');
    return `${datePart} ${hh}:00–${nextH}:00`;
  }
  const [year, month, day] = periodStart.split('-').map(Number);
  if (period === 'year') return String(year);
  if (period === 'month') {
    return new Date(Date.UTC(year, month - 1, 1))
      .toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  }
  return new Date(Date.UTC(year, month - 1, day))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** True when "next period" would land on now or later — what disables the > button. */
export function isCurrentOrFuturePeriod(periodStart: string, period: Period): boolean {
  if (period === 'hour') {
    const d = asUtc(periodStart);
    const now = new Date();
    const periodHourStart = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours());
    const currentHourStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours());
    return periodHourStart >= currentHourStart;
  }
  const [year, month, day] = periodStart.split('-').map(Number);
  const now = new Date();
  const ny = now.getUTCFullYear(), nm = now.getUTCMonth() + 1, nd = now.getUTCDate();
  if (period === 'year') return year >= ny;
  if (period === 'month') return year > ny || (year === ny && month >= nm);
  return year > ny || (year === ny && month > nm) || (year === ny && month === nm && day >= nd);
}

/**
 * The chart selection (bucket indices) as the ISO range a drill-down asks for.
 * `end` is EXCLUSIVE and covers the whole of the last selected bucket — an end
 * at the bucket's start would silently drop everything inside it.
 */
export function brushToDateRange(
  brushRange: [number, number] | null,
  period: Period,
  periodStart: string,
  bucketCount: number,
): { start: string; end: string } {
  const startIdx = brushRange ? brushRange[0] : 0;
  const endIdx = brushRange ? brushRange[1] : bucketCount - 1;

  const base = period === 'hour' && periodStart.includes('T')
    ? asUtc(periodStart)
    : new Date(periodStart + 'T00:00:00Z');

  let startDate: Date;
  let endDate: Date;
  if (period === 'hour') {
    startDate = new Date(base.getTime() + startIdx * 60_000);
    endDate = new Date(base.getTime() + (endIdx + 1) * 60_000);
  } else if (period === 'day') {
    startDate = new Date(base.getTime() + startIdx * 3_600_000);
    endDate = new Date(base.getTime() + (endIdx + 1) * 3_600_000);
  } else if (period === 'month') {
    startDate = new Date(base.getTime() + startIdx * 86_400_000);
    endDate = new Date(base.getTime() + (endIdx + 1) * 86_400_000);
  } else {
    startDate = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + startIdx, 1));
    endDate = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + endIdx + 1, 1));
  }
  return { start: startDate.toISOString(), end: endDate.toISOString() };
}

/**
 * The buckets that have actually happened. The backend fills a whole period, so
 * "this month" arrives with a flat zero line running to the 31st — which reads
 * as a collapse in usage rather than as days that have not occurred yet.
 */
export function bucketsSoFar<T>(buckets: T[], period: Period, periodStart: string | null | undefined): T[] {
  if (!buckets.length || !periodStart) return buckets;
  const base = period === 'hour' && periodStart.includes('T')
    ? asUtc(periodStart)
    : new Date(periodStart + 'T00:00:00Z');
  // An hour of slack, so the bucket in progress is never cut off.
  const cutoff = new Date(Date.now() + 3_600_000);
  let maxBuckets: number;
  if (period === 'hour') maxBuckets = Math.ceil((cutoff.getTime() - base.getTime()) / 60_000);
  else if (period === 'day') maxBuckets = Math.ceil((cutoff.getTime() - base.getTime()) / 3_600_000);
  else if (period === 'month') maxBuckets = Math.ceil((cutoff.getTime() - base.getTime()) / 86_400_000);
  else maxBuckets = (cutoff.getUTCFullYear() - base.getUTCFullYear()) * 12 + cutoff.getUTCMonth() - base.getUTCMonth() + 1;
  return buckets.slice(0, Math.min(buckets.length, Math.max(1, maxBuckets)));
}
